// Convert the project's extracted styleguide tokens into a Breakdance
// Global Settings JSON payload. Owners can paste this into Breakdance →
// Settings → Global Settings → Import (or use the file download path).
//
// Shape locked from a real Breakdance "Default Template" export:
//
//   {
//     "settings": {
//       "colors":     { "palette": { "colors": [{ cssVariableName, label, value }, …] } },
//       "typography": { base_size, ratio, heading_font, body_font, advanced: { … } }
//     }
//   }
//
// Each palette entry needs a unique `cssVariableName` of the form
// `bde-palette-color-<N>-<uuid>` where N is 1..4 (the meaning of N isn't
// documented; the real export uses an apparently-random spread, so we
// match the format with `1` and a fresh uuid per color).
//
// Typography is best-effort: the most-used font becomes both heading +
// body slugged into Breakdance's `gfont-<name>` convention (Google Fonts
// is the default loader). Base size is the most-common px declaration in
// the 14–20px body-text range, falling back to 16px.

import type { ApiStyleguideTokens, StyleColor, StyleFont, StyleSize } from "@clarity/shared";

type PaletteColor = {
  cssVariableName: string;
  label: string;
  value: string;
};

type BreakdanceGlobalSettings = {
  settings: {
    colors: { palette: { colors: PaletteColor[] } };
    typography: {
      base_size: { breakpoint_base: { number: number; unit: "px"; style: string } };
      ratio: { breakpoint_base: number };
      heading_font: string;
      body_font: string;
      advanced: {
        body: {
          typography: {
            custom: {
              customTypography: {
                lineHeight: {
                  breakpoint_base: { number: string; unit: "custom"; style: string };
                };
              };
            };
          };
        };
        headings: {
          all_headings: {
            typography: {
              custom: {
                customTypography: {
                  lineHeight: {
                    breakpoint_base: { number: string; unit: "custom"; style: string };
                  };
                };
              };
            };
          };
        };
      };
    };
  };
};

export function toBreakdanceGlobalSettings(tokens: ApiStyleguideTokens): string {
  const payload: BreakdanceGlobalSettings = {
    settings: {
      colors: { palette: { colors: buildPalette(tokens.colors) } },
      typography: buildTypography(tokens.fonts, tokens.sizes),
    },
  };
  return JSON.stringify(payload, null, 2);
}

// ---------- palette ----------

function buildPalette(colors: StyleColor[]): PaletteColor[] {
  return colors.map((c, i) => ({
    cssVariableName: `bde-palette-color-1-${uuid()}`,
    label: paletteLabel(c.value, i),
    value: c.value.toUpperCase(),
  }));
}

// Friendly label generated from the hex. We don't try to invent
// "Brand 500" / "Neutral 100" names — those are subjective and would be
// wrong as often as right. Designers can rename in Breakdance.
function paletteLabel(hex: string, index: number): string {
  const n = String(index + 1).padStart(2, "0");
  return `Color ${n} (${hex.toUpperCase()})`;
}

function uuid(): string {
  // crypto.randomUUID is available in every modern browser the dashboard
  // runs in. Falls back to a low-quality v4 if not (e.g. very old WebViews).
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------- typography ----------

function buildTypography(
  fonts: StyleFont[],
  sizes: StyleSize[],
): BreakdanceGlobalSettings["settings"]["typography"] {
  const primary = fonts[0]?.family ?? "Inter";
  const slug = `gfont-${slugify(primary)}`;
  const baseSize = pickBaseSize(sizes);

  return {
    base_size: {
      breakpoint_base: {
        number: baseSize,
        unit: "px",
        style: `${baseSize}px`,
      },
    },
    ratio: { breakpoint_base: 1.25 },
    heading_font: slug,
    body_font: slug,
    advanced: {
      body: {
        typography: {
          custom: {
            customTypography: {
              lineHeight: {
                breakpoint_base: { number: "1.5", unit: "custom", style: "1.5" },
              },
            },
          },
        },
      },
      headings: {
        all_headings: {
          typography: {
            custom: {
              customTypography: {
                lineHeight: {
                  breakpoint_base: { number: "1.2", unit: "custom", style: "1.2" },
                },
              },
            },
          },
        },
      },
    },
  };
}

function slugify(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Pick the most-common px-unit size that lands in the body-text range
// (14–20px). Ties broken by the StyleSize array's existing frequency
// order (sizes come in pre-sorted by `count` from the worker). Falls
// back to 16px if nothing matches.
function pickBaseSize(sizes: StyleSize[]): number {
  const pxSizes = sizes
    .map((s) => {
      const m = /^(\d+(?:\.\d+)?)px$/.exec(s.value);
      if (!m) return null;
      const n = Number(m[1]);
      if (!Number.isFinite(n)) return null;
      return { n, count: s.count };
    })
    .filter((s): s is { n: number; count: number } => s !== null);
  const inRange = pxSizes.filter((s) => s.n >= 14 && s.n <= 20);
  const best = inRange[0] ?? pxSizes[0];
  return best ? Math.round(best.n) : 16;
}
